/**
 * HARDENED TIER RESOLVER + ENTITLEMENTS MODULE
 * 
 * SINGLE SOURCE OF TRUTH for tier resolution and entitlements across the entire app.
 * This module is importable by both server and client code.
 * 
 * CANONICAL TIER SOURCE (Authoritative):
 * - PRIMARY: user.subscriptionTier (updated by Stripe webhooks, lowercase: "free" | "pack_leader")
 * - SECONDARY: user.planTier (synced with subscriptionTier, uppercase: "FREE" | "PACK_LEADER")
 * 
 * Both fields are updated synchronously by Stripe webhooks, but subscriptionTier is the
 * authoritative source for product gating and billing.
 * 
 * TIER MAPPING:
 * - FREE / null / undefined → "Tail Wagger" badge, free entitlements
 * - PACK_LEADER → "Pack Leader" badge, pack_leader entitlements
 * - TOP_DOG → "Top Dog" badge, top_dog entitlements (future tier, supported now)
 */

/**
 * Canonical tier enum (authoritative)
 * These are the ONLY valid tier values in the system.
 */
export type TierEnum = 'FREE' | 'PACK_LEADER' | 'TOP_DOG'

/**
 * Badge label type (user-facing display)
 */
export type BadgeLabel = 'Tail Wagger' | 'Pack Leader' | 'Top Dog'

/**
 * Subscription tier type (for backward compatibility with existing code)
 * Lowercase version used by server-side gating
 */
export type SubscriptionTier = 'free' | 'pack_leader'

/**
 * Tier resolution result
 */
export interface TierResolution {
  tier: TierEnum
  badgeLabel: BadgeLabel
  subscriptionTier: SubscriptionTier // Lowercase for server-side compatibility
}

/**
 * Feature entitlements per tier
 */
export interface TierEntitlements {
  picks: {
    aiFit: boolean
    fullAnalysis: boolean
    smartSearch: boolean
  }
  aiChat: {
    enabled: boolean
    enhancedContext: boolean
  }
  parks: {
    safetyIntelligence: boolean
    whyThisPark: boolean
    smartWalk: boolean
    photoUpload: boolean
  }
}

/**
 * Normalize raw tier value to canonical TierEnum
 * 
 * Handles:
 * - Case variations (FREE, free, Free)
 * - Legacy strings (null, undefined, empty)
 * - String variations (pack_leader, PACK_LEADER, Pack Leader)
 * - Future tiers (top_dog, TOP_DOG, Top Dog)
 * 
 * @param rawTier - Raw tier value from database (subscriptionTier or planTier)
 * @returns Canonical TierEnum
 */
export function normalizeTier(rawTier: string | null | undefined): TierEnum {
  if (!rawTier) {
    return 'FREE'
  }

  // Normalize: lowercase, trim, replace spaces with underscores
  const normalized = String(rawTier).trim().toLowerCase().replace(/\s+/g, '_')

  // Map to canonical enum values
  if (normalized === 'pack_leader' || normalized === 'packleader') {
    return 'PACK_LEADER'
  }

  if (normalized === 'top_dog' || normalized === 'topdog') {
    return 'TOP_DOG'
  }

  // FREE is default for any unrecognized value (fail-safe)
  return 'FREE'
}

/**
 * Resolve tier and badge label from user profile
 * 
 * This is the SINGLE SOURCE OF TRUTH for tier resolution.
 * All badge components and entitlement checks MUST use this function.
 * 
 * CANONICAL FIELD PRIORITY:
 * 1. subscriptionTier (primary, updated by webhooks, authoritative for billing)
 * 2. planTier (secondary, synced with subscriptionTier)
 * 
 * @param userProfile - User profile object with tier fields
 * @returns TierResolution with tier enum, badge label, and subscription tier
 */
function tierRank(tier: TierEnum): number {
  if (tier === 'TOP_DOG') return 2
  if (tier === 'PACK_LEADER') return 1
  return 0
}

function readNormalizedTier(raw: string | null | undefined): TierEnum | null {
  if (raw == null || String(raw).trim() === '') return null
  return normalizeTier(raw)
}

export function resolveTier(userProfile?: {
  subscriptionTier?: string | null
  planTier?: string | null
} | null): TierResolution {
  // Consider both fields and prefer the highest tier. Legacy rows may have stale
  // subscriptionTier "free" while planTier is still PACK_LEADER.
  const subNorm = readNormalizedTier(userProfile?.subscriptionTier)
  const planNorm = readNormalizedTier(userProfile?.planTier)
  let tier: TierEnum = 'FREE'
  for (const candidate of [subNorm, planNorm]) {
    if (candidate != null && tierRank(candidate) > tierRank(tier)) {
      tier = candidate
    }
  }

  // Map tier to badge label
  const badgeLabel: BadgeLabel = 
    tier === 'PACK_LEADER' ? 'Pack Leader' :
    tier === 'TOP_DOG' ? 'Top Dog' :
    'Tail Wagger' // FREE or unknown → Tail Wagger

  // Map to lowercase subscription tier for server-side compatibility
  const subscriptionTier: SubscriptionTier = 
    tier === 'PACK_LEADER' ? 'pack_leader' :
    tier === 'TOP_DOG' ? 'pack_leader' : // Top Dog gets pack_leader entitlements for now
    'free'

  return { tier, badgeLabel, subscriptionTier }
}

/**
 * Get badge label directly (convenience function)
 * 
 * @param userProfile - User profile object with tier fields
 * @returns Badge label string
 */
export function getBadgeLabel(userProfile?: {
  subscriptionTier?: string | null
  planTier?: string | null
} | null): BadgeLabel {
  return resolveTier(userProfile).badgeLabel
}

/**
 * Get entitlements for a tier
 * 
 * @param tier - Tier enum
 * @returns TierEntitlements object with feature flags
 */
export function getEntitlements(tier: TierEnum): TierEntitlements {
  if (tier === 'PACK_LEADER' || tier === 'TOP_DOG') {
    return {
      picks: {
        aiFit: true,
        fullAnalysis: true,
        smartSearch: true,
      },
      aiChat: {
        enabled: true,
        enhancedContext: true,
      },
      parks: {
        safetyIntelligence: true,
        whyThisPark: true,
        smartWalk: true,
        photoUpload: true,
      },
    }
  }

  // FREE tier entitlements
  return {
    picks: {
      aiFit: false, // Preview only
      fullAnalysis: false,
      smartSearch: false,
    },
    aiChat: {
      enabled: true, // Free tier can use chat
      enhancedContext: false,
    },
    parks: {
      safetyIntelligence: false,
      whyThisPark: false,
      smartWalk: false,
      photoUpload: true, // Free tier: 1 photo per month (trial)
    },
  }
}

/**
 * Check if user has entitlement for a specific feature
 * 
 * @param userProfile - User profile object with tier fields
 * @param featurePath - Dot-separated path to feature (e.g., 'picks.aiFit', 'aiChat.enabled')
 * @returns true if user has entitlement, false otherwise
 */
export function hasEntitlement(
  userProfile: { subscriptionTier?: string | null; planTier?: string | null } | null | undefined,
  featurePath: string
): boolean {
  const { tier } = resolveTier(userProfile)
  const entitlements = getEntitlements(tier)
  
  const [category, feature] = featurePath.split('.')
  if (!category || !feature) {
    console.warn('[TierResolver] Invalid feature path:', featurePath)
    return false
  }

  const categoryEntitlements = entitlements[category as keyof TierEntitlements]
  if (!categoryEntitlements || typeof categoryEntitlements !== 'object') {
    return false
  }

  return (categoryEntitlements as any)[feature] === true
}

/**
 * Tier mismatch guard - logs warning if tier values are inconsistent
 * 
 * @param userProfile - User profile object with tier fields
 * @returns Resolved tier (uses canonical source, logs mismatch if detected)
 */
export function resolveTierWithGuard(userProfile?: {
  subscriptionTier?: string | null
  planTier?: string | null
} | null): TierResolution {
  const resolution = resolveTier(userProfile)
  
  // Check for tier mismatch (both fields present but inconsistent)
  if (userProfile?.subscriptionTier && userProfile?.planTier) {
    const subTier = normalizeTier(userProfile.subscriptionTier)
    const planTier = normalizeTier(userProfile.planTier)
    
    if (subTier !== planTier) {
      console.warn('[TierResolver] Tier mismatch detected:', {
        subscriptionTier: userProfile.subscriptionTier,
        planTier: userProfile.planTier,
        resolvedSubscriptionTier: subTier,
        resolvedPlanTier: planTier,
        usingCanonical: 'subscriptionTier',
        finalTier: resolution.tier,
      })
    }
  }
  
  return resolution
}
